// wizard-step-chrome.tsx — shared chrome for the campaign wizard steps.
//
// Every step (Audience / Message / Timing / Review) centres its content in the
// SAME narrow column and opens with the SAME heading shape: a soft colour-tinted
// icon badge (a little "quest marker" that pops in on entry) over a centred
// title + subtitle. That shared shape is what makes the four steps read as one
// flow. Lives in its own module so both wizard.tsx and drip-audience-step.tsx
// can use it without a runtime import cycle (the WizardStepId import is
// type-only, so it erases at compile time).

import React from 'react';
import { Users, MessageSquare, Clock, Rocket, type LucideIcon } from 'lucide-react';
import { TINTS } from './primitives';
import type { WizardStepId } from './wizard';

// Shared centred content width — matches the Audience step's reused list column
// so every step has the same generous side breathing room.
export const WIZARD_STEP_MAX = 760;

// Per-step badge icon + colour tint. Distinct tints give the flow a colourful,
// gamified progression rather than four identical blue headers.
export const STEP_BADGE: Record<WizardStepId, { icon: LucideIcon; tint: keyof typeof TINTS }> = {
  audience: { icon: Users, tint: 'cyan' },
  message: { icon: MessageSquare, tint: 'blue' },
  timing: { icon: Clock, tint: 'amber' },
  review: { icon: Rocket, tint: 'green' },
};

// The soft tinted badge on its own — used inside StepHeading and standalone
// above the Audience step's reused header.
export const StepBadge: React.FC<{ step: WizardStepId; size?: number }> = ({ step, size = 52 }) => {
  const meta = STEP_BADGE[step];
  const t = TINTS[meta.tint] || TINTS.blue;
  const I = meta.icon;
  return (
    <div className="pop" style={{
      width: size, height: size, borderRadius: size * 0.3, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: t.bg, color: t.fg, border: `1px solid ${t.line}`,
      // The tint's own pre-mixed drop (see TINTS) — the old form appended an
      // alpha suffix to a hex literal, which a themed `var()` cannot carry.
      boxShadow: `0 6px 18px -8px ${t.glow}`,
    }}>
      <I size={size * 0.44} />
    </div>
  );
};

// Centred step heading: badge → title → subtitle. Matches the Audience step's
// CampaignTabHeader (2xl bold title, small subtitle) with the badge added on top.
interface StepHeadingProps {
  step: WizardStepId;
  title: string;
  subtitle?: React.ReactNode;
}
export const StepHeading: React.FC<StepHeadingProps> = ({ step, title, subtitle }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 26 }}>
    <StepBadge step={step} />
    <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 14 }}>{title}</h2>
    {subtitle && (
      <p style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 6, maxWidth: 460, lineHeight: 1.5 }}>
        {subtitle}
      </p>
    )}
  </div>
);
